import React from 'react';
import { Loader } from 'lucide-react';

/**
 * Loading Component
 * 
 * A unified loading component for displaying loading states across the application.
 * Supports various sizes and contexts (full page, inline, button).
 * 
 * @param {string} size - Loading size: 'sm' | 'md' | 'lg'
 * @param {string} variant - Loading variant: 'spinner' | 'icon'
 * @param {string} text - Optional loading text
 * @param {string} className - Additional CSS classes
 * @param {boolean} fullPage - Whether to display as full page loading
 * @param {boolean} inline - Whether to display inline (no centering)
 */
const Loading = ({ 
  size = 'md',
  variant = 'spinner',
  text,
  className = '',
  fullPage = false,
  inline = false
}) => {
  const sizeConfig = {
    sm: {
      spinner: 'w-4 h-4',
      icon: 'w-4 h-4',
      text: 'text-tiny',
      container: 'gap-2'
    },
    md: {
      spinner: 'w-8 h-8',
      icon: 'w-6 h-6',
      text: 'text-caption',
      container: 'gap-3'
    },
    lg: {
      spinner: 'w-10 h-10',
      icon: 'w-8 h-8',
      text: 'text-body',
      container: 'gap-4'
    }
  };

  const config = sizeConfig[size] || sizeConfig.md;

  const spinnerElement = variant === 'spinner' ? (
    <div 
      className={`${config.spinner} border-2 border-accent/30 border-t-accent rounded-full animate-spin`}
      aria-label="Loading"
    />
  ) : (
    <Loader 
      className={`${config.icon} text-accent animate-spin`}
      aria-label="Loading"
    />
  );

  const content = (
    <div className={`flex ${inline ? 'items-center' : 'flex-col items-center'} ${config.container} ${className}`}>
      {spinnerElement}
      {text && (
        <p className={`${config.text} text-ink-secondary`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        {content}
      </div>
    );
  }

  if (inline) {
    return content;
  }

  return (
    <div className="flex items-center justify-center">
      {content}
    </div>
  );
};

/**
 * Loading Spinner (for buttons and inline use)
 * Compact spinner without text, perfect for buttons
 */
export const LoadingSpinner = ({ size = 'sm', className = '' }) => {
  const sizeConfig = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div 
      className={`${sizeConfig[size] || sizeConfig.sm} border-2 border-white/20 border-t-white rounded-full animate-spin ${className}`}
      aria-label="Loading"
    />
  );
};

export default Loading;